import { ArrowRight, Mail, MessageCircle, Phone } from 'lucide-react'

import { type Region, SNOOTS_HQ, SNOOTS_US } from '@snootsvet/shared'

import { useRegion } from '@/customer/hooks/use-region'

/**
 * Inline support CTA. UK members get a WhatsApp link; US members get email, call, and text links
 * when the request is urgent. Drop inside an `AlertAction` slot or use standalone.
 */
export function ContactSupportAction({
  prefill,
  region: regionOverride,
  urgent = false,
}: {
  prefill?: string
  region?: Region
  urgent?: boolean
} = {}) {
  const region = useRegion({ override: regionOverride })

  if (region.id === 'uk') {
    const href = prefill
      ? `${SNOOTS_HQ.whatsappUrl}?text=${encodeURIComponent(prefill)}`
      : SNOOTS_HQ.whatsappUrl

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="group text-right text-button-sm underline underline-offset-2 hover:no-underline"
      >
        Message the team on WhatsApp
        <ArrowRight
          className="ml-1 inline size-3 align-middle transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </a>
    )
  }

  const emailHref = prefill
    ? `mailto:${SNOOTS_US.email}?body=${encodeURIComponent(prefill)}`
    : `mailto:${SNOOTS_US.email}`

  if (urgent) {
    return (
      <span className="flex flex-col items-end gap-1 text-right text-button-sm">
        <span className="inline-flex items-center gap-1 font-bold">
          Contact the team
          <ArrowRight className="size-3" aria-hidden />
        </span>
        <a
          href={`tel:${SNOOTS_US.phone.replace(/\D/g, '')}`}
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:no-underline"
        >
          <Phone className="size-3" aria-hidden />
          Call {SNOOTS_US.phone}
        </a>
        <a
          href={`sms:${SNOOTS_US.textPhone.replace(/\D/g, '')}`}
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:no-underline"
        >
          <MessageCircle className="size-3" aria-hidden />
          Text {SNOOTS_US.textPhone}
        </a>
        <a
          href={emailHref}
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:no-underline"
        >
          <Mail className="size-3" aria-hidden />
          {SNOOTS_US.email}
        </a>
      </span>
    )
  }

  return (
    <span className="text-right text-button-sm">
      Email us at{' '}
      <a href={emailHref} className="underline underline-offset-2 hover:no-underline">
        {SNOOTS_US.email}
      </a>
    </span>
  )
}
